<template>
    <WorkScheduleContainerRow>
        <CompanyEmployeeNameCell v-if="companyEmployee" :company="company!" :companyEmployee="companyEmployee" class="column stickyLeft" />
        <CompanyNameCell v-else-if="company" :company="company" class="column stickyLeft" />
        <ResourceNameCell v-else :resource="resource!" :externalRoster="otherRoster ? externalRosterName : undefined" :roster="roster" class="column stickyLeft" />
        <WorkScheduleContainerColumn timeGrid>
            <template v-for="(rowsOnDay, dayIndex) in deploymentsPerDayPerRow" :key="dayIndex">
                <template v-for="(deploymentRow, rowIndex) in rowsOnDay">
                    <DeploymentRow
                        v-for="deployment in deploymentRow"
                        :key="'deployment' + deployment.id"
                        :rowIndex="rowIndex"
                        :renderedDateRange="renderedDateRange"
                        :deployment="deployment"
                        :organisationDeploymentStates="organisationDeploymentStates"
                        :groupLabels="groupLabels"
                        :resource="resource"
                        :company="company"
                        :shift="shifts[deployment.shiftId] ?? undefined"
                        :timeBlocksWithoutWorkScheduleAssignments="timeBlocksWithoutWorkScheduleAssignmentsByDeploymentId[deployment.id!] ?? []"
                        :unavailableForDrop="deploymentsNotAvailableForDrop.includes(deployment.id!)"
                        :specificationsNotAvailableForDrop="specificationsNotAvailableForDrop"
                        :workScheduleAssignments="workScheduleAssignmentsByDeployment[deployment.id!] ?? []"
                        :workSchedulePositions="workSchedulePositions"
                        :workingDeploymentStates="workingDeploymentStates"
                        :visibleDeploymentSpecificationTypeIds="visibleDeploymentSpecificationTypeIds"
                        @dragStart:workScheduleAssignment="$emit('dragStart:workScheduleAssignment', $event)"
                        @dragEnd:workScheduleAssignment="$emit('dragEnd:workScheduleAssignment', $event)"
                        @reload:workSchedulePosition="$emit('reload:workSchedulePosition', $event)"
                    />
                </template>
            </template>
        </WorkScheduleContainerColumn>
    </WorkScheduleContainerRow>
</template>

<script lang="ts">
    import {PlanbordWorkSchedulePosition, ResourceWithKeyedByDateObjects, CompanyWithDeploymentsAndEmployees, CompanyEmployeeWithDeployments, PlanbordWorkScheduleGroupLabel} from '@/types/api-object-extensions';
    import DeploymentRow from '@/components/WorkSchedule/DeploymentRow.vue';
    import {DateRange, RosterData, RosterDay} from '@/types/types';
    import {Deployment, OrganisationDeploymentState, Shift, DeploymentStateSpecification, WorkScheduleAssignment} from '@cquential/api';
    import WorkScheduleUtil from '@/util/WorkScheduleUtil';
    import CompanyEmployeeNameCell from '@/components/Planbord/CompanyEmployeeNameCell.vue';
    import CompanyNameCell from '@/components/Planbord/CompanyNameCell.vue';
    import ResourceNameCell from '@/components/Planbord/ResourceNameCell.vue';
    import WorkScheduleContainerRow from '@/components/WorkSchedule/WorkScheduleContainerRow.vue';
    import WorkScheduleContainerColumn from '@/components/WorkSchedule/WorkScheduleContainerColumn.vue';
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import DeploymentUtil from '@/util/deploymentutil';

    @Component({
        components: {
            DeploymentRow,
            CompanyEmployeeNameCell,
            WorkScheduleContainerRow,
            CompanyNameCell,
            ResourceNameCell,
            WorkScheduleContainerColumn,
        },
        emits: [
            'dragStart:workScheduleAssignment',
            'dragEnd:workScheduleAssignment',
            'reload:workSchedulePosition',
        ],
    })
    export default class DeploymentsHolderRow extends Vue {
        @Prop({required: true}) public renderedDateRange!: DateRange;
        @Prop({default: []}) public deploymentsNotAvailableForDrop!: number[];
        @Prop({default: []}) public specificationsNotAvailableForDrop!: number[];
        @Prop({required: true}) public groupLabels!: Record<number, PlanbordWorkScheduleGroupLabel>;
        @Prop({required: true}) public organisationDeploymentStates!: Record<number, OrganisationDeploymentState>;
        @Prop() public resource?: ResourceWithKeyedByDateObjects;
        @Prop({required: true}) public shifts!: Record<number, Shift>;
        @Prop({required: true}) public timeBlocksWithoutWorkScheduleAssignmentsByDeploymentId!: Record<number, DateRange[]>;
        @Prop({required: true}) public workScheduleAssignmentsByDeployment!: Record<number, WorkScheduleAssignment[]>;
        @Prop({required: true}) public workSchedulePositions!: Record<number, PlanbordWorkSchedulePosition>;
        @Prop({required: true}) public workingDeploymentStates!: DeploymentStateSpecification[];
        @Prop({required: true}) public visibleDeploymentSpecificationTypeIds!: number[];
        @Prop({type: Boolean}) public otherRoster!: boolean;
        @Prop({required: true}) public rosterNames!: Record<number, string>;
        @Prop({required: true}) public roster!: RosterData;
        @Prop() public company?: CompanyWithDeploymentsAndEmployees;
        @Prop() public companyEmployee?: CompanyEmployeeWithDeployments;

        public static readonly WorkScheduleUtil: typeof WorkScheduleUtil = WorkScheduleUtil;

        public get renderedRosterDays(): RosterDay[] {
            return WorkScheduleUtil.convertDateRangeToRosterDays(this.renderedDateRange, this.roster);
        }

        public get deploymentsPerDayPerRow(): Deployment[][][] {
            return Object.values(this.renderedRosterDays).map(this.deploymentsPerRow);
        }

        public get externalRosterName(): string {
            return this.rosterNames![this.resource!.contractedTimes![0].rosterId] || 'Onbekend rooster';
        }

        public deploymentsPerRow(day: RosterDay): Deployment[][] {
            const deployments: Record<string, Deployment[]> | undefined = this.resource?.deployments ?? this.companyEmployee?.deployments ?? this.company?.deploymentsWithoutEmployee;
            if (!deployments) {
                return [];
            }

            let deploymentsForDay: Deployment[] = deployments[day.formattedDateIso]?.filter(this.canAssignWorkSchedulesOnDeployment) ?? [];
            deploymentsForDay = deploymentsForDay.filter((deployment: Deployment): boolean => {
                if (deployment.specifications!.at(-1)!.end < this.renderedDateRange.begin!) {
                    return false;
                } else if (deployment.specifications![0]!.begin > this.renderedDateRange.end!) {
                    return false;
                }

                return true;
            });

            return this.splitOverlappingDeploymentsIntoRows(deploymentsForDay);
        }

        private canAssignWorkSchedulesOnDeployment(deployment: Deployment): boolean {
            if (deployment.specifications!.length === 0) {
                return false;
            }

            return deployment.shiftId in this.shifts && this.shifts[deployment.shiftId].rosterId === this.roster.rosterId;
        }

        private splitOverlappingDeploymentsIntoRows(deployments: Deployment[]): Deployment[][] {
            const deploymentRows: Deployment[][] = [];
            for (const deployment of DeploymentUtil.sortOnSpecificationBeginTimesAscending(deployments)) {
                const rowIndex: number = this.findFirstIndexRowWithoutOverlap(deploymentRows, deployment);
                if (!(rowIndex in deploymentRows)) {
                    deploymentRows[rowIndex] = [];
                }
                deploymentRows[rowIndex].push(deployment);
            }

            return deploymentRows;
        }

        private findFirstIndexRowWithoutOverlap(deploymentRows: Deployment[][], deployment: Deployment): number {
            for (let i: number = 0; i < deploymentRows.length; i++) {
                const row: Deployment[] = deploymentRows[i];
                if (!row.some((alreadyAddedDeployment: Deployment): boolean => this.deploymentsOverlap(alreadyAddedDeployment, deployment))) {
                    return i;
                }
            }

            return deploymentRows.length;
        }

        private deploymentsOverlap(deploymentInRow: Deployment, deploymentToAdd: Deployment): boolean {
            const beginTime: Date = deploymentToAdd.specifications![0].begin;
            const endTime: Date = deploymentToAdd.specifications![deploymentToAdd.specifications!.length - 1].end;
            const existingDeploymentBeginTime: Date = deploymentInRow.specifications![0].begin;
            const existingDeploymentEndTime: Date = deploymentInRow.specifications![deploymentInRow.specifications!.length - 1].end;

            return endTime > existingDeploymentBeginTime && beginTime < existingDeploymentEndTime;
        }
    }
</script>

<style lang="scss" scoped>
    .column {
        padding: var(--cell-padding);
        border-bottom: 1px solid var(--work-schedule-border-color);

        &.stickyLeft {
            position: sticky;
            left: 0;
            z-index: 2;
            background: var(--modal-background);
            border-right: 1px solid var(--work-schedule-border-color);
        }
    }
</style>
